import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClaimsToSign } from '../../ui/ClaimsToSign';
import {
  CARE_STAFF_ROLES,
  ROLE_LABEL,
  api,
  benefitItemUpdate,
  benefitItems,
  benefitReference,
  careRequestAcknowledge,
  careRequestComplete,
  careRequests,
  feeSchedule,
  incidentReports,
  medPass,
  medPassScan,
  medPassRecord,
  medPassStrike,
  medPassUndo,
  nutritionWorklist,
  onShiftWithMe,
  partANotes,
  partASign,
  recordChairStand,
  recordWeight,
  referralCreate,
  referralUpdate,
  referrals,
  reporterChecklist,
  residentsRoster,
  worklistActions,
  worklistAtRisk,
  worklistBoard,
  worklistCapture,
  worklistCodes,
  worklistCreateItem,
  worklistHandoff,
  worklistMeasures,
  worklistCover,
  worklistCoverAccept,
  worklistCoverPass,
  worklistCoverTakeBack,
  worklistCoverage,
  facilityMedWatches,
  medWatchStepDone,
  medWatchSign,
  type ActionBody,
  type ApiClient,
  type CoverItemRef,
  type ApiError,
  type CareRequest,
  type ItemRef,
  type MeAssignment,
  type MedPassNeeds,
  type MedRuleRefusal,
  type MedPassRow,
  type Referral,
  type WorklistItem,
} from '@aithora/shared';
import { MedPassSheet, type MedSheet, type MedSheetResult } from './MedPassSheet';
import { NurseToolsRow, PocToast, type PocMode } from './nurse/PocDialog';
import { InboxSheet, ReportIncidentSheet } from './nurse/IncidentSheets';
import { ScanSheet } from './nurse/ScanSheet';
import { CountSheet } from './nurse/CountSheet';
import { SayItOnceSheet } from './nurse/SayItOnceSheet';
import { ChartSheet } from './nurse/ChartSheet';
import { TrendsSheet } from './nurse/TrendsSheet';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { facilitiesParam, usePickedIds } from '../../session/FacilityScope';
import { ScopeEmpty, nothingFrom } from '../../ui/ScopeEmpty';
import { useQuery, type QueryState } from '../../useQuery';
import { Tabs } from '../../ui/Tabs';
import { useReadOnly, useReadOnlyReason } from '../../ui/ReadOnly';
import { ErrorState, Skeleton } from '../../ui/states';
import { A, HonestyText, sx, type AxActions } from './parts';
import {
  HANDS,
  REFUSALS,
  SNOOZES,
  TAB_LABEL,
  WCHK,
  clockText,
  codeIndex,
  exitOfItem,
  homeTab,
  isFiled,
  isHandled,
  makeAx,
  measureGroups,
  measureRow,
  medRows,
  noteRows,
  nutView,
  orderTabs,
  periodClosesToday,
  refDone,
  requestRow,
  requestStatus,
  riskIndex,
  daysLeft,
  type Ax,
  type Exit,
  type Lens,
  type RefDraft,
  type TabKey,
  type ViewKey,
} from './model';
import { cfComing, cfView, type CfCandidate, type CfComingRow, type CfTask } from './careFlow';
import { LadderLine, MovingUpBox } from './ladder';
import { BenefitsTab, MeasureGroups, MeasuresTab, MedPassTab, NotesTab, NutritionTab, ReferralsTab, RequestsTab } from './tabs';
import {
  callOutFeed,
  coverageCard,
  coverTags,
  liveLines,
  medSafetyFeed,
  myCredentialsFeed,
  mySurveyFeed,
  readResume,
  receiverCards,
  senderCards,
  shiftBriefFeed,
  staffingLine,
  watchHead,
  watchSignRows,
  watchSteps,
  writeResume,
  type CoverLookup,
  type WatchSignRow,
  type WatchStepRow,
} from './v9';
import {
  AlsoTodayRow,
  CoverOversight,
  MoreRow,
  NotConnectedCard,
  ReceiverCardView,
  ReceiverFrame,
  ResumeLine,
  SafetyCards,
  SenderCardView,
  ShiftBriefRow,
  StaffingSummary,
  TonightStaffing,
  ToolsToggle,
  ViewEmpty,
  WatchSignsBlock,
  WatchStepsBlock,
} from './v9parts';
import { useNavigate } from 'react-router-dom';
import './worklist.css';
import { userKey } from '../../session/store';
import { tierLabel } from '../residents/common';

// A port of the Console V3 design's Worklist (template T1333-1991, logic L36050-36533,
// L37643-38330, the action layer wlLayer L34453 and the tickers L33881) over the live
// routes. The design's first-run coach (T1334, wcShow) is switched off by the design
// itself (wlLayer: "if (role === 'Care Staff') o.wcShow = false", and no other role ever
// shows it), so it is not ported; the Care Staff setup line on the Overview replaces it.

export const WORKLIST_POLL_MS = 30_000;

/** A proposed route the back end does not serve yet answers 404: treat it as empty. */
function optional<T>(p: Promise<T>): Promise<T | null> {
  return p.catch((e: ApiError) => {
    if (e && e.kind === 'not_found') return null;
    throw e;
  });
}

/**
 * A block this viewer may not read (the medicine watch's clinical steps for the Aithora team
 * without a granted break-glass) answers 403: the block is left out, like a route not served.
 */
function allowed<T>(p: Promise<T>): Promise<T | null> {
  return p.catch((e: ApiError) => {
    if (e && (e.kind === 'not_found' || e.kind === 'forbidden')) return null;
    throw e;
  });
}

function useNow(stepMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), stepMs);
    return () => window.clearInterval(t);
  }, [stepMs]);
  return now;
}

function lensOf(role: string | null): Lens {
  if (role === 'ADMIN') return 'admin';
  if (role && CARE_STAFF_ROLES.has(role as never)) return 'staff';
  return 'ro';
}

// Note drafts can name residents: kept for this tab and this person only (sessionStorage,
// under their own key), and removed at sign-out (store.ts clearPerUserData).
function readDrafts(key: string | null): Record<string, string> {
  if (!key) return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(key) || '{}') || {};
  } catch {
    return {};
  }
}
function writeDrafts(key: string | null, d: Record<string, string>) {
  if (!key) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(d));
  } catch {
    /* a convenience only */
  }
}

/**
 * A medication rule's warning when the building is in warn mode: the dose IS recorded, so
 * the words say that, not the refusal sentence strict mode uses.
 */
export function warnWords(reason: string): string {
  if (/without a current license or permit/i.test(reason)) return 'Recorded with a warning: no current license or permit is on file for you, so a nurse should review it.';
  // The rule's citation stays (unless it is only our conservative default); the instruction
  // to ask the nurse goes, since the dose is already recorded for a nurse to review.
  const cite = /\(([^)]*)\)\s*$/.exec(reason)?.[1] ?? null;
  const core = reason.replace(/\s*\([^)]*\)\s*$/, '').replace(/\s*Ask the nurse\.?\s*$/i, '').trim().replace(/\.$/, '');
  const keep = cite && !/conservative default/i.test(cite) ? ` (${cite})` : '';
  return `Recorded with a warning, for a nurse to review: ${core}${keep}.`;
}

/** Items per POST /worklist/actions call (the server's own limit). */
export const ACTION_CHUNK = 100;

/** Why the bulk actions wait: nothing on the list is selected. */
const NOTHING_SELECTED = 'Select one or more rows first (the box on each row, or X).';

export function WorklistPage() {
  const { role, realRole } = useConsoleRole();
  const { client, session } = useSession();
  const draftsKey = userKey('aithora.wq.drafts', session?.user.id);
  const roleReadOnly = useReadOnly();
  const roReason = useReadOnlyReason();
  const baseLens = lensOf(role);
  const [preview, setPreview] = useState(false);
  const lens: Lens = preview ? 'staff' : baseLens;
  // The design's "ro": Aithora and Partner read every building and write nothing here.
  const ro = baseLens === 'ro';
  const writesOff = roleReadOnly || ro || preview;
  // What the rows draw: in a preview the Care Staff controls show, switched off.
  const shownRo = ro && !preview;
  const offReason = roReason ?? (preview ? 'Preview only: you are seeing the Care Staff view, and nothing is sent.' : ro ? `Read-only as ${tierLabel(role ?? 'OPERATOR')}` : null);
  const token = session?.token;
  const meId = session?.user.id ?? null;
  const now = useNow(1000);
  const phone = typeof window !== 'undefined' && window.innerWidth < 600;
  const wide = typeof window !== 'undefined' ? window.innerWidth >= 1280 : true;

  const load = <T,>(fn: (c: ApiClient, s: AbortSignal) => Promise<T>, deps: unknown[], opts: { pollMs?: number; enabled?: boolean } = {}): QueryState<T> =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuery<T>((s) => fn(client, s), [client, token, ...deps], opts);

  // ── Which building ────────────────────────────────────────────────────────
  // Aithora (and a Super Admin viewing as a facility role) has no building of its own: it picks one from the board.
  const sessionOrg = session?.user.organizationId ?? null;
  const needPick = baseLens === 'ro' || !sessionOrg;
  // The facility switcher (V6): the board, and so the building list, is the picked buildings.
  const facilities = facilitiesParam(usePickedIds());
  const board = load((c, signal) => worklistBoard(c, { query: facilities ? { facilities } : undefined, signal }), [facilities], { enabled: baseLens !== 'staff' || needPick, pollMs: WORKLIST_POLL_MS });
  const [pickedFacility, setPickedFacility] = useState<string | null>(null);
  const boardFacilities = board.data?.facilities ?? [];
  // A building chosen here that the switcher has since left out is dropped for the first in view.
  const chosen = pickedFacility && boardFacilities.some((f) => f.facilityId === pickedFacility) ? pickedFacility : null;
  const facilityId = needPick ? (chosen ?? boardFacilities[0]?.facilityId ?? null) : sessionOrg;
  const orgQ = needPick && facilityId ? { organizationId: facilityId } : {};
  const has = !!facilityId;

  // ── Data (every tab: the tab row is ordered by what is outstanding in each) ──
  const measures = load((c, signal) => worklistMeasures(c, { params: { id: facilityId! }, signal }), [facilityId], { enabled: has, pollMs: WORKLIST_POLL_MS });
  const fees = load((c, signal) => feeSchedule(c, { signal }), []);
  const codesAll = load((c, signal) => worklistCodes(c, { query: { limit: 100 }, signal }), []);
  const atRisk = load((c, signal) => worklistAtRisk(c, { query: needPick && facilityId ? { facility: facilityId } : {}, signal }), [facilityId], { enabled: has, pollMs: WORKLIST_POLL_MS });
  const reqs = load((c, signal) => careRequests(c, { query: { status: 'all', ...orgQ }, signal }), [facilityId], { enabled: has, pollMs: WORKLIST_POLL_MS });
  const meds = load((c, signal) => medPass(c, { query: orgQ, signal }), [facilityId], { enabled: has, pollMs: WORKLIST_POLL_MS });
  const notes = load((c, signal) => optional(partANotes(c, { query: needPick && facilityId ? { facility: facilityId } : {}, signal })), [facilityId], { enabled: has });
  const nut = load((c, signal) => optional(nutritionWorklist(c, { query: needPick && facilityId ? { facility: facilityId } : {}, signal })), [facilityId], { enabled: has });
  const bens = load((c, signal) => benefitItems(c, { query: { status: 'all', limit: 200, ...orgQ }, signal }), [facilityId], { enabled: has });
  const benRef = load((c, signal) => optional(benefitReference(c, { signal })), []);
  const refs = load((c, signal) => referrals(c, { query: orgQ, signal }), [facilityId], { enabled: has && lens !== 'staff' });
  // The reporter checklist from the server (restored from production): its optional steps
  // show beside the design's four requirements.
  const refChecklist = load((c, signal) => reporterChecklist(c, { signal }), [], { enabled: has && lens !== 'staff' });
  const assignment = load<MeAssignment | null>((c, signal) => api.meAssignment(c, { signal }), [], { enabled: baseLens === 'staff' });
  const roster = load((c, signal) => residentsRoster(c, { signal }), [], { enabled: baseLens !== 'ro' });
  const onShift = load((c, signal) => optional(onShiftWithMe(c, { signal })), [], { enabled: baseLens !== 'ro' });
  // Nurse tools: Care Staff only (V7 file pwLayer: role === 'Care Staff'); the Facility
  // Admin's Worklist opens straight on its tabs. Aithora and Partner read the Worklist only;
  // a preview draws the tools switched off.
  const toolsShown = baseLens === 'staff' || preview;
  const toolsLive = baseLens === 'staff' && !writesOff && has;
  const inbox = load((c, signal) => incidentReports(c, { query: { status: 'draft' }, signal }), [], { enabled: toolsLive, pollMs: WORKLIST_POLL_MS });
  const [poc, setPoc] = useState<PocMode | null>(null);
  const [pocToast, setPocToast] = useState<string | null>(null);
  // V9 "Pulled away" cover: what moved to me and what of mine moved (building staff only; the
  // route refuses the read-only roles), and the 30-day figure for the Facility Admin and the
  // read-only roles (the route refuses care staff).
  const coverLive = (baseLens === 'staff' || baseLens === 'admin') && !preview && has;
  const cover = load((c, signal) => allowed(worklistCover(c, { signal })), [], { enabled: coverLive, pollMs: WORKLIST_POLL_MS });
  const coverage = load((c, signal) => allowed(worklistCoverage(c, { params: { id: facilityId! }, signal })), [facilityId], { enabled: baseLens !== 'staff' && !preview && has, pollMs: WORKLIST_POLL_MS });
  // V8 new medicine watch: steps for a nurse, the Facility Admin and the Aithora team; the signs for care staff.
  const watches = load((c, signal) => allowed(facilityMedWatches(c, { params: { id: facilityId! }, signal })), [facilityId], { enabled: has, pollMs: WORKLIST_POLL_MS });
  const navigate = useNavigate();

  const mine = useMemo(() => {
    const a = assignment.data?.assignment;
    return baseLens === 'staff' && a ? new Set(a.residents.map((r) => r.id)) : null;
  }, [assignment.data, baseLens]);
  const inScope = useCallback((residentId: string | null) => !mine || !residentId || mine.has(residentId), [mine]);

  // ── Page state ────────────────────────────────────────────────────────────
  const [tabState, setTab] = useState<TabKey | null>(null);
  const [view, setView] = useState<ViewKey>('All');
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [menu, setMenu] = useState<{ id: string; kind: 'snz' | 'hand' | 'ref' } | null>(null);
  const [localExits, setLocalExits] = useState<Record<string, Exit>>({});
  const [toast, setToast] = useState<{ ids: string[]; refs: Record<string, ItemRef>; text: string; t: number } | null>(null);
  const [hoOpen, setHoOpen] = useState(false);
  const [hoSent, setHoSent] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  // Med pass (AF-1611): the one sheet a dose may need, and the 10-second Undo after a record.
  const [medSheet, setMedSheet] = useState<MedSheet | null>(null);
  const [medToast, setMedToast] = useState<{ id: string; text: string } | null>(null);
  const [justDone, setJustDone] = useState<Record<string, string>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>(() => readDrafts(draftsKey));
  const [wqMode, setWqMode] = useState<'deadline' | 'resident'>('deadline');
  const [codeQ, setCodeQ] = useState('');
  const [scopeAll, setScopeAll] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ what: '', res: '', owner: 'me', cad: 'once' as 'once' | 'daily' | 'weekly' | 'monthly', due: 'Today', kind: 'task' as 'task' | 'vitals', at: '' });
  const [reqFilter, setReqFilter] = useState<'Open' | 'All'>('Open');
  const [reqSrc, setReqSrc] = useState('All');
  const [brief, setBrief] = useState(false);
  const [nutSel, setNutSel] = useState(0);
  const [nutWt, setNutWt] = useState('');
  const [nutCh, setNutCh] = useState('');
  const [benKind, setBenKind] = useState('All');
  const [benRefOpen, setBenRefOpen] = useState(false);
  const [refSel, setRefSel] = useState<number>(0);
  const [refDrafts, setRefDrafts] = useState<Record<string, RefDraft>>({});
  const [refCase, setRefCase] = useState<Record<string, string>>({});
  // V7 "Moving up the ladder": the whole list open, or the first three.
  const [cfOpen, setCfOpen] = useState(false);
  // V9 "Summary first": search, codes, views and keys behind one button, for every role.
  const [tools, setTools] = useState(false);
  // V9: the staffing line's Details (null: closed, unless a hand-off reaches this Facility Admin).
  const [stOpen, setStOpen] = useState<boolean | null>(null);
  // A sign reported from the medicine watch, until the next read (the signs view does not list reports).
  const [reported, setReported] = useState<Record<string, string>>({});
  const tab: TabKey = tabState && (lens !== 'staff' || tabState === 'requests' || tabState === 'medpass') ? tabState : homeTab(lens);
  // V9 "Pick up where you left off": the tab this person was on last time, in this browser.
  const resumeOn = (baseLens === 'staff' || baseLens === 'admin') && !preview;
  const [resumePrev] = useState(() => readResume(meId));
  const [resumeGone, setResumeGone] = useState(false);
  useEffect(() => {
    if (!resumeOn) return;
    writeResume(meId, tab);
    if (tab === resumePrev) setResumeGone(true);
  }, [resumeOn, meId, tab, resumePrev]);

  const searchQ = codeQ.trim();
  const codeHits = load((c, signal) => worklistCodes(c, { query: { q: searchQ, limit: 30 }, signal }), [searchQ], { enabled: searchQ.length >= 2 && !writesOff });

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 10_000);
    return () => window.clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    if (!pocToast) return;
    const t = window.setTimeout(() => setPocToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [pocToast]);
  useEffect(() => {
    if (!medToast) return;
    const t = window.setTimeout(() => setMedToast(null), 10_000);
    return () => window.clearTimeout(t);
  }, [medToast]);

  // ── Rows for every tab ────────────────────────────────────────────────────
  const codes = useMemo(() => codeIndex(fees.data?.codes ?? []), [fees.data]);
  const risk = useMemo(() => riskIndex(atRisk.data), [atRisk.data]);
  const roleName = tierLabel(role ?? 'OPERATOR');
  const axFor = (tab: TabKey, sourceId: string, who: string, label: string, dueMs: number | null, hasDone: boolean, full: boolean, serverExit: Exit | null): Ax => {
    const id = `${tab}:${sourceId}`;
    const ax = makeAx({ tab, id, who, label, dueMs, hasDone, full, exit: serverExit ?? localExits[id] ?? null }, { ro: shownRo, phone, roleName: preview ? 'a preview' : roleName, now, selected: !!sel[id] });
    const rk = risk.get(id);
    if (rk && ax.live) {
      ax.risk = true;
      ax.riskWhy = rk.why;
      ax.score = rk.score;
    }
    return ax;
  };

  // ── V7 escalation ladder (care-flow.js): who holds each task and who it moves to ──
  const people = (onShift.data?.people ?? []).filter((p) => !!p.name).map((p) => ({ userId: p.userId, name: p.name as string }));
  const ladderCharge = (onShift.data?.people ?? []).find((p) => p.onCallRoles.includes('CHARGE_NURSE'))?.name ?? null;
  /** A hand-off a person made to a person is a claim: "I'll take it", or "Give to …". */
  function claimOf(i: WorklistItem): string | null {
    const la = i.lastAction;
    if (!la || la.action !== 'HANDOFF' || la.auto || !i.ownerId) return null;
    return i.ownerId === meId ? 'You' : i.ownerName ?? 'Someone';
  }
  const OWNER_WORDS: Record<string, string> = { CAREGIVER: 'Floor staff', STAFF: 'Floor staff', AIDE: 'Any CNA', CHARGE_NURSE: 'Charge nurse', ADMIN: 'Facility Admin', ADMIN_ON_DUTY: 'Administrator on duty' };
  const cap = (w: string | null | undefined) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : null);
  // The server runs the ladder (ladder-rule.ts) and sends each row's level, step and next move:
  // the console words that state and keeps no clock of its own.
  const measureCf = (i: WorklistItem): CfTask => ({
    id: `wq-${i.id}`,
    view: cfView(i.ladder, i.task, i.dueAt, { closed: i.status === 'done' || i.status === 'refused' }),
    owner: i.ownerId ? (i.ownerId === meId ? 'You' : i.ownerName ?? 'Someone') : i.ownerRole ? OWNER_WORDS[i.ownerRole] ?? null : null,
    charge: ladderCharge,
    done: i.status === 'done' || !!justDone[i.id],
    claimedBy: claimOf(i),
    firstOwner: i.firstOwnerId && i.firstOwnerId === meId ? 'You' : cap(i.firstOwnerName),
  });
  const nameOfUser = (id: string | null) => (!id ? null : id === meId ? 'You' : people.find((p) => p.userId === id)?.name ?? 'Someone');
  const requestCf = (q: CareRequest): CfTask => {
    const handed = localExits[`requests:${q.id}`];
    const by = q.acknowledged_by ? nameOfUser(q.acknowledged_by) : handed?.st === 'hand' ? handed.to ?? null : null;
    const open = requestStatus(q.status) !== 'DONE';
    const view = cfView(q.ladder, `${q.type} ${q.description ?? q.title}`, q.created_at, { urgent: q.urgency === 'URGENT', closed: !open });
    return {
      id: `rq-${q.id}`,
      // A hand-off made here shows at once; the server's view names it on the next poll.
      view: by && !view.claimedAt && handed?.at ? { ...view, claimedAt: new Date(handed.at).toISOString() } : view,
      // A request reaches the floor staff on the resident's assignment: on Care Staff's list it is theirs.
      owner: lens === 'staff' ? 'You' : 'Floor staff',
      charge: ladderCharge,
      done: !open,
      claimedBy: open ? by : null,
    };
  };

  const measureItems = (measures.data?.items ?? []).filter((i) => i.status !== 'done' || justDone[i.id]);
  const mRows = measureItems.map((i) => ({
    row: measureRow(i, { now, meId, ro: shownRo, codes, justDone: justDone[i.id] ?? null }),
    ax: axFor('measures', i.id, i.residentName ?? 'Facility-wide', i.task, Date.parse(i.dueAt), false, false, claimOf(i) === 'You' ? null : exitOfItem(i, meId)),
  }));
  const nRows = noteRows((notes.data?.rows ?? []).filter((r) => inScope(r.residentId)), now).map((row) => ({ row, ax: axFor('notes', row.key, row.name, 'Part A note', row.dueAt, false, false, null) }));
  const allReqs = (reqs.data?.careRequests ?? []).filter((q) => inScope(q.resident_id));
  const qRowsAll = allReqs.map((q) => requestRow(q, now));
  const qRows = qRowsAll
    .filter((q) => (reqFilter === 'All' ? true : q.isOpen))
    .filter((q) => reqSrc === 'All' || q.src === reqSrc)
    .sort((p, q) => (p.isOpen === q.isOpen ? 0 : p.isOpen ? -1 : 1) || (p.isUrg === q.isUrg ? 0 : p.isUrg ? -1 : 1) || p.since - q.since)
    .map((row) => ({ row, ax: axFor('requests', row.id, row.name, row.kind, null, false, false, null) }));
  const dRows = medRows(meds.data?.meds ?? [], now).map((row) => ({ row, ax: axFor('medpass', row.key, row.res, row.name, row.due, false, false, null) }));
  const nutRowsAll = (nut.data?.worklist.rows ?? []).map((n, i) => nutView(n, i, now));
  const uViews = nutRowsAll
    .slice()
    .sort((p, q) => (p.inWindow === q.inWindow ? 0 : p.inWindow ? -1 : 1) || (p.dueAt ?? 0) - (q.dueAt ?? 0))
    .map((row) => ({ row, ax: axFor('nutrition', row.row.residentId, row.name, 'nutrition review', null, true, true, null) }));
  const benAll = bens.data?.items ?? [];
  const bRows = benAll.map((b) => ({ b, ax: axFor('benefits', b.id, b.residentLabel ?? 'Facility-wide', b.title, null, false, false, null) }));
  const refAll = refs.data?.referrals ?? [];
  const fRows = refAll.map((r, i) => ({ r, i, ax: axFor('referrals', r.id, r.residentName ?? 'Resident', 'referral', null, true, true, null) }));

  // The action layer's view filter (All / At risk / Handed off / Selected) and snoozes.
  const keep = (ax: Ax) => !ax.snzOn && (view === 'All' || (view === 'At risk' && ax.risk) || (view === 'Handed off' && ax.exit?.st === 'hand') || (view === 'Selected' && !!sel[ax.id]));
  const allAx: Ax[] = [...mRows, ...nRows, ...qRows, ...dRows, ...uViews, ...bRows, ...fRows].map((r) => r.ax);
  const openAx = allAx.filter((a) => a.live);
  const riskAx = openAx.filter((a) => a.risk).sort((p, q) => p.score - q.score);

  // "Moving up the ladder": requests and measures together, on both tabs (design wlLayer `flat`).
  const cfCands: CfCandidate[] = [
    ...allReqs.map((q) => ({ task: requestCf(q), who: q.residentName ?? 'Resident', room: q.room ?? null, what: q.type, ref: { kind: 'req' as const, q } })),
    ...measureItems.map((i) => ({ task: measureCf(i), who: i.residentName ?? 'Facility-wide', room: i.room, what: i.task, ref: { kind: 'meas' as const, i } })),
  ];
  // Whether the server runs the ladder (the route says so); off, no row shows a countdown.
  const ladderOn = measures.data?.ladderOn ?? reqs.data?.ladderOn;
  const coming = cfComing(cfCands, { now, canAssign: !shownRo, open: cfOpen, staff: people, ladderOn });

  // ── Tab counts (design L37651) ────────────────────────────────────────────
  const nutCodable = nutRowsAll.filter((n) => n.state === 'CODABLE').length;
  const nutFnOnly = nutRowsAll.filter((n) => n.state === 'FUNCTION ONLY').length;
  const counts: Record<TabKey, number> = {
    // Measures: open coded items whose billing period closes today (design wMeasOpen: a
    // monthly code closes with the month, a half-yearly with the half, a yearly with the year).
    measures: (measures.data?.items ?? []).filter((i) => (i.status === 'open' || i.status === 'snoozed') && !!i.code && periodClosesToday(fees.data?.codes.find((c) => c.code === i.code)?.rateUnit ?? null, now)).length,
    notes: nRows.filter((n) => n.row.late && !n.row.signed).length,
    // Care Staff's "Your tasks" holds their requests and their own scheduled tasks.
    requests: qRowsAll.filter((q) => q.isOpen).length + (lens === 'staff' ? measureItems.filter((i) => i.status === 'open' || i.status === 'snoozed').length : 0),
    medpass: dRows.filter((m) => !m.row.recorded).length,
    nutrition: nutCodable + nutFnOnly,
    // The design never raises Benefits by count: its failure mode is a missed date.
    benefits: 0,
    referrals: refAll.filter((r) => !isFiled(r)).length,
  };
  const tabs = orderTabs(lens, counts);

  // ── Writes ───────────────────────────────────────────────────────────────
  const reloadAll = () => {
    measures.reload();
    atRisk.reload();
    reqs.reload();
    meds.reload();
    notes.reload();
    nut.reload();
    bens.reload();
    refs.reload();
  };
  const act = async (fn: () => Promise<unknown>) => {
    if (writesOff || busy) return false;
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      reloadAll();
      return true;
    } catch (e) {
      setActionError(e as ApiError);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // ── Med pass (AF-1611): Given records at once; the server says when an order needs more ──
  const recordMed = async (m: MedPassRow, status: 'GIVEN' | 'MISSED' | 'REFUSED' | 'HELD' | 'OBSERVED', fields: Record<string, string> = {}, assisted = false) => {
    const r = await medPassRecord(client, { body: { medicationId: m.medication_id, residentId: m.resident_id, status, medicationName: m.name, ...fields, ...(assisted ? { assisted } : {}) } });
    // A building in 'warn' mode records the dose and says which medication rule it broke
    // (med-rules/): said as recorded-with-a-warning, never as if it was refused.
    const what = status === 'OBSERVED' ? 'Noted'
      : assisted ? `Assisted · ${status === 'GIVEN' ? 'taken' : status === 'REFUSED' ? 'refused' : 'missed'}`
      : status === 'GIVEN' ? 'Given' : 'Not given';
    setMedToast({ id: r.id, text: `${what}: ${m.name ?? 'medicine'}${r.medRuleWarning ? '. ' + warnWords(r.medRuleWarning.reason) : ''}` });
    meds.reload();
  };
  // Given, or Assisted · taken on an order set to Assistance: records at once; the server
  // asks for what the order needs (422), refuses what the rules or the order's service level
  // do not allow (403, 409), and either answer opens the sheet with its own words.
  const giveMed = (m: MedPassRow, assisted = false) =>
    void act(async () => {
      try {
        await recordMed(m, 'GIVEN', {}, assisted);
      } catch (e) {
        const body = (e as ApiError).body as MedPassNeeds | undefined;
        if ((e as ApiError).status === 422 && Array.isArray(body?.needs)) {
          setMedSheet({ mode: 'given', med: m, needs: body!, ...(assisted ? { assisted } : {}) });
          return;
        }
        // The building's medication rules do not let this person record it: the server's reason.
        const refusal = (e as ApiError).body as MedRuleRefusal | undefined;
        if ((e as ApiError).status === 403 && refusal?.medRule) {
          setMedSheet({ mode: 'refused', med: m, reason: refusal.error, citation: refusal.medRule.citation });
          return;
        }
        // The order's service level does not take this record (an Administer order, a hold rule).
        const level = (e as ApiError).body as { error?: string; code?: string } | undefined;
        if ((e as ApiError).status === 409 && level?.code === 'service_level' && level.error) {
          setMedSheet({ mode: 'refused', med: m, reason: level.error, citation: null });
          return;
        }
        throw e;
      }
    });
  const submitMedSheet = async (r: MedSheetResult) => {
    const sh = medSheet;
    if (!sh) return;
    if (r.kind === 'strike') {
      if (sh.mode !== 'strike') return;
      await medPassStrike(client, { params: { id: sh.administrationId }, body: r.note ? { reason: r.reason, note: r.note } : { reason: r.reason } });
      meds.reload();
      return;
    }
    await recordMed(sh.med, r.status, r.fields, r.assisted === true);
  };
  const undoMed = (id: string) =>
    void act(async () => {
      setMedToast(null);
      await medPassUndo(client, { params: { id } });
    });

  const refOf = (ax: Ax): ItemRef => {
    const sourceId = ax.id.slice(ax.tab.length + 1);
    if (ax.tab === 'measures') {
      const it = measures.data?.items.find((i) => i.id === sourceId);
      return { tab: 'tasks', sourceId, occurrence: it?.occurrence ?? null };
    }
    return { tab: ax.tab as ItemRef['tab'], sourceId };
  };
  const exitAction = (axs: Ax[], body: Omit<ActionBody, 'itemRef' | 'itemRefs'>, exit: Omit<Exit, 'at' | 'by' | 'snzN'>, text: string) =>
    act(async () => {
      // Nothing selected is never sent: the server would answer with its own words.
      if (!axs.length) throw Object.assign(new Error(NOTHING_SELECTED), { kind: 'validation', requestId: null });
      const refsById = Object.fromEntries(axs.map((a) => [a.id, refOf(a)]));
      // The server takes at most ACTION_CHUNK items a call: a larger selection goes in
      // chunks, in order, and is reported once.
      const okIds: string[] = [];
      let failed = 0;
      for (let i = 0; i < axs.length; i += ACTION_CHUNK) {
        const part = axs.slice(i, i + ACTION_CHUNK);
        const b: ActionBody = axs.length === 1 ? { ...body, itemRef: refsById[part[0].id] } : { ...body, itemRefs: part.map((a) => refsById[a.id]) };
        const r = await worklistActions(client, { body: b }).catch((e: unknown) => {
          // A refusal is said in plain words, never the server's own string.
          const st = (e as { status?: number }).status;
          if (st === 400 || st === 422) {
            const done = okIds.length ? ` ${okIds.length} of ${axs.length} were recorded before it stopped.` : '';
            throw Object.assign(new Error((okIds.length ? 'Nothing more was recorded' : 'Nothing was recorded') + ': those rows could not be read. Select them again and retry.' + done), { kind: 'validation', requestId: (e as { requestId?: string | null }).requestId ?? null });
          }
          throw e;
        });
        part.forEach((a, j) => (r.results[j]?.ok !== false ? okIds.push(a.id) : (failed += 1)));
      }
      setLocalExits((p) => {
        const n = { ...p };
        for (const id of okIds) n[id] = { ...exit, at: Date.now(), by: 'You', snzN: ((p[id]?.snzN ?? 0) + (exit.st === 'snz' ? 1 : 0)) } as Exit;
        return n;
      });
      setMenu(null);
      setSel((p) => {
        const n = { ...p };
        for (const id of okIds) delete n[id];
        return n;
      });
      setToast({ ids: okIds, refs: refsById, text: axs.length === 1 ? text : `${text} · ${okIds.length} items`, t: Date.now() });
      if (failed) throw Object.assign(new Error(`${failed} of ${axs.length} could not be recorded.`), { kind: 'validation', requestId: null });
    });
  const undo = (ids: string[], refsById: Record<string, ItemRef>) =>
    act(async () => {
      for (const id of ids) await worklistActions(client, { body: { itemRef: refsById[id], action: 'UNDO' } });
      setLocalExits((p) => {
        const n = { ...p };
        for (const id of ids) delete n[id];
        return n;
      });
      setToast(null);
    });

  // ── The ladder's actions, all real writes: a request is taken by acknowledging it; a task by a
  // hand-off to me; "Give to" is a hand-off to that person (the server checks they are on staff here).
  const orgBody = needPick && facilityId ? { organizationId: facilityId } : undefined;
  const takeTask = (ref: { kind: 'req'; q: CareRequest } | { kind: 'meas'; i: WorklistItem }) =>
    void act(async () => {
      if (ref.kind === 'req') await careRequestAcknowledge(client, { params: { id: ref.q.id }, body: orgBody });
      else await worklistActions(client, { body: { itemRef: { tab: 'tasks', sourceId: ref.i.id, occurrence: ref.i.occurrence }, action: 'HANDOFF', handedTo: { userId: meId ?? undefined } } });
    });
  const giveTask = (ref: { kind: 'req'; q: CareRequest } | { kind: 'meas'; i: WorklistItem }, userId: string, name: string) => {
    const ax = allAx.find((a) => a.id === (ref.kind === 'req' ? `requests:${ref.q.id}` : `measures:${ref.i.id}`));
    if (ax) void exitAction([ax], { action: 'HANDOFF', handedTo: { userId } }, { st: 'hand', to: name }, `Handed to ${name} · ${ax.who}`);
  };
  const undoClaim = (i: WorklistItem) => void act(() => worklistActions(client, { body: { itemRef: { tab: 'tasks', sourceId: i.id, occurrence: i.occurrence }, action: 'UNDO' } }));
  const ladderOf = (row: CfComingRow) => row.ref as { kind: 'req'; q: CareRequest } | { kind: 'meas'; i: WorklistItem };
  const movingUp = (
    <MovingUpBox
      c={coming}
      canAssign={!shownRo}
      onMore={() => setCfOpen((v) => !v)}
      onTake={(row) => takeTask(ladderOf(row))}
      onGive={(row, userId, name) => giveTask(ladderOf(row), userId, name)}
      disabled={writesOff || busy}
      title={offReason ?? undefined}
    />
  );
  const measureLadder = (id: string) => {
    const i = measureItems.find((x) => x.id === id);
    if (!i) return null;
    const t = measureCf(i);
    return (
      <LadderLine
        task={t}
        now={now}
        canClaim={!shownRo}
        onClaim={() => takeTask({ kind: 'meas', i })}
        onUndo={shownRo ? null : () => undoClaim(i)}
        disabled={writesOff || busy}
        title={offReason ?? undefined}
      />
    );
  };
  const requestLadder = (id: string) => {
    const q = allReqs.find((x) => x.id === id);
    if (!q) return null;
    const ax = allAx.find((a) => a.id === `requests:${q.id}`);
    const handedHere = !!localExits[`requests:${q.id}`] && !q.acknowledged_by;
    // Acknowledge is the request's own claim; an acknowledgement has no undo on the server.
    return <LadderLine task={requestCf(q)} now={now} hideLevel canClaim={false} onUndo={!shownRo && handedHere && ax ? () => void undo([ax.id], { [ax.id]: refOf(ax) }) : null} disabled={writesOff || busy} title={offReason ?? undefined} />;
  };

  // A task already late snoozes once, for at most 10 minutes (the server's rule, items.ts LATE_SNOOZE_MINUTES).
  const lateNow = (ax?: Ax) => !!ax && ax.mins !== null && ax.mins < 0;
  const snoozeLabel = (i: number, late: boolean) => (late ? `${SNOOZES[i][0].split(' · ')[0]} · 10 min` : SNOOZES[i][0]);
  const menuPicks = (kind: 'snz' | 'hand' | 'ref', ax?: Ax) => (kind === 'snz' ? SNOOZES.map((_z, i) => snoozeLabel(i, lateNow(ax))) : kind === 'hand' ? HANDS.map((h) => h[0]) : REFUSALS);
  const axActs: AxActions = {
    onSel: (id) => setSel((p) => ({ ...p, [id]: !p[id] })),
    onDone: (ax) => void exitAction([ax], { action: 'DONE' }, { st: 'done' }, `✓ Done · ${ax.who}`),
    onMenu: (id, kind) => setMenu((m) => (m && m.id === id && m.kind === kind ? null : { id, kind })),
    onPick: (ax, kind, i) => {
      if (kind === 'snz') {
        const [, hours, reason] = SNOOZES[i];
        const late = lateNow(ax);
        const label = snoozeLabel(i, late);
        const ms = late ? 10 * 60_000 : hours * 3_600_000;
        void exitAction([ax], { action: 'SNOOZE', reason, snoozeUntil: new Date(Date.now() + ms).toISOString() }, { st: 'snz', why: label, until: Date.now() + ms }, `Snoozed · ${ax.who} · ${label}`);
      } else if (kind === 'hand') {
        const [label, roleKey] = HANDS[i];
        void exitAction([ax], { action: 'HANDOFF', handedTo: { role: roleKey } }, { st: 'hand', to: label }, `Handed to ${label} · ${ax.who}`);
      } else {
        const why = REFUSALS[i];
        void exitAction([ax], { action: 'REFUSED', reason: why }, { st: 'ref', why }, `Refused · ${ax.who} · ${why}`);
      }
    },
    onUndo: (ax) => void undo([ax.id], { [ax.id]: refOf(ax) }),
    menu,
    menuPicks,
    disabled: writesOff || busy,
    disabledReason: offReason,
  };

  // Keyboard, desk only: J / K move, D / S / H / R act, X selects (design wlLayer).
  const focusRef = useRef<string | null>(null);
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const tg = (ev.target as HTMLElement | null)?.tagName;
      if (tg === 'INPUT' || tg === 'TEXTAREA' || tg === 'SELECT' || ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const k = (ev.key || '').toLowerCase();
      if (k.length !== 1 || 'jkdshrx'.indexOf(k) < 0) return;
      const els = Array.prototype.filter.call(document.querySelectorAll('[data-ax]'), (x: HTMLElement) => x.offsetParent) as HTMLElement[];
      if (!els.length) return;
      ev.preventDefault();
      let i = Math.max(-1, els.findIndex((x) => x.getAttribute('data-ax') === focusRef.current));
      if (k === 'j' || k === 'k') {
        i = k === 'j' ? Math.min(els.length - 1, i + 1) : Math.max(0, i - 1);
        els.forEach((x) => {
          if (x.parentElement) x.parentElement.style.outline = '';
        });
        const el = els[i];
        focusRef.current = el.getAttribute('data-ax');
        if (el.parentElement) {
          el.parentElement.style.outline = '2px solid #2F5C97';
          el.parentElement.style.outlineOffset = '-2px';
          el.parentElement.scrollIntoView?.({ block: 'nearest' });
        }
        return;
      }
      const el = els[Math.max(0, i)];
      const b = el.querySelector<HTMLElement>(`[data-k="${k}"]`);
      if (b) b.click();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Tab-specific writes ───────────────────────────────────────────────────
  const capture = (id: string, value: string | number, text: string) =>
    act(async () => {
      const it = measures.data?.items.find((i) => i.id === id);
      await worklistCapture(client, { params: { id }, body: { value, occurrence: it?.occurrence } });
      setJustDone((p) => ({ ...p, [id]: text }));
      window.setTimeout(() => setJustDone((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      }), 1600);
      setDrafts((p) => {
        const n = { ...p };
        delete n[id];
        writeDrafts(draftsKey, n);
        return n;
      });
    });
  const setDraft = (id: string, v: string) =>
    setDrafts((p) => {
      const n = { ...p, [id]: v };
      writeDrafts(draftsKey, n);
      return n;
    });

  const ownerOpts = useMemo(() => {
    const people = (onShift.data?.people ?? []).map((p) => ({ key: `u:${p.userId}`, label: p.name ?? 'Colleague' }));
    const base = [{ key: 'me', label: 'You' }, ...(lens === 'admin' ? people : []), { key: 'r:AIDE', label: 'Any CNA' }, { key: 'r:CHARGE_NURSE', label: 'Any nurse' }, { key: 'none', label: 'Nobody' }];
    return base;
  }, [onShift.data, lens]);
  const addItem = (what: string, code: string | null) =>
    act(async () => {
      const o = newItem.owner;
      const body = {
        what,
        code,
        residentId: newItem.res || (roster.data?.residents[0]?.id ?? null),
        cadence: newItem.cad,
        ...(newItem.kind === 'vitals' ? { taskType: 'VITALS' as const } : {}),
        ...(/^\d{2}:\d{2}$/.test(newItem.at) ? { timeOfDay: newItem.at } : {}),
        dueWindowHours: ({ Today: 8, 'Within 3 days': 72, 'This week': 168, 'This month': 720 } as Record<string, number>)[newItem.due] ?? 8,
        ...(o === 'me' ? { ownerUserId: meId } : o.startsWith('u:') ? { ownerUserId: o.slice(2) } : o.startsWith('r:') ? { ownerRole: o.slice(2) } : {}),
      };
      await worklistCreateItem(client, { params: { id: facilityId! }, body });
      setAdding(false);
      setNewItem((p) => ({ ...p, what: '' }));
      setCodeQ('');
    });

  const sendHandoff = () =>
    act(async () => {
      const lines = [
        `Still open: ${openAx.length}, at risk: ${riskAx.length}`,
        ...riskAx.slice(0, 8).map((a) => `At risk: ${a.who}, ${a.riskWhy}`),
      ].join('\n');
      await worklistHandoff(client, { body: { summary: lines, toRole: 'CHARGE_NURSE' } });
      setHoSent(Date.now());
    });

  // ── Render ───────────────────────────────────────────────────────────────
  const first = measures.loading && !measures.data && !measures.error && has;
  if ((needPick && board.loading && !board.data) || first) {
    return (
      <div className="wl" data-testid="worklist">
        <Skeleton rows={8} label="Loading the worklist" />
      </div>
    );
  }
  if (needPick && !facilityId) {
    if (facilities && board.data && !board.data.facilities.length) {
      return (
        <div className="wl" data-testid="worklist">
          <ScopeEmpty title={nothingFrom} body="None of the buildings in this view has a worklist in Aithora yet." />
        </div>
      );
    }
    return (
      <div className="wl" data-testid="worklist">
        {board.error ? <ErrorState error={board.error} onRetry={board.reload} /> : <p style={sx('font-size:16px;color:#54606C;')}>No building has a worklist yet.</p>}
      </div>
    );
  }

  const boardTotals = boardFacilities.reduce(
    (a, f) => ({ n: a.n + 1, total: a.total + f.total, done: a.done + f.done, overdue: a.overdue + f.overdue, atRisk: a.atRisk + f.atRisk }),
    { n: 0, total: 0, done: 0, overdue: 0, atRisk: 0 },
  );
  const boardText = board.data
    ? `Today across ${boardTotals.n} facilit${boardTotals.n === 1 ? 'y' : 'ies'}: ${boardTotals.total ? Math.round((boardTotals.done / boardTotals.total) * 100) : 0}% done · ${boardTotals.overdue} overdue · ${boardTotals.atRisk} at risk`
    : '';
  const riskChips = (wide ? riskAx.slice(0, 4) : riskAx.slice(0, 1)).map((a) => ({ a, why: a.riskWhy.split(' · ').slice(1).join(' · ') }));
  const moreRisk = riskAx.length > (wide ? 4 : 1);
  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const selAx = allAx.filter((a) => sel[a.id]);
  const bb = 'display:inline-flex;align-items:center;min-height:36px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
  const goRisk = (a: Ax) => {
    setTab(a.tab);
    setView('All');
    window.setTimeout(() => {
      // design go(): bring the row to 60px below the top of its scroller and ring it red for 1.6s
      const el = document.querySelector(`[data-ax="${a.id}"]`);
      const row = el?.parentElement as HTMLElement | null | undefined;
      if (!row) return;
      let sc: HTMLElement | null = row;
      while (sc && !(sc.scrollHeight > sc.clientHeight + 4 && /auto|scroll/.test(getComputedStyle(sc).overflowY))) sc = sc.parentElement;
      if (sc) sc.scrollTop += row.getBoundingClientRect().top - sc.getBoundingClientRect().top - 60;
      row.style.transition = 'box-shadow .3s';
      row.style.boxShadow = 'inset 3px 0 0 #B3261E, 0 0 0 2px rgba(179,38,30,.25)';
      window.setTimeout(() => { row.style.boxShadow = ''; }, 1600);
    }, 120);
  };

  const snoozed = allAx.filter((a) => a.snzOn);
  const refused = allAx.filter((a) => a.exit?.st === 'ref');
  const handed = allAx.filter((a) => a.exit?.st === 'hand');
  const byTab: Partial<Record<TabKey, number>> = {};
  for (const a of openAx) byTab[a.tab] = (byTab[a.tab] ?? 0) + 1;
  const hoGroups: { head: string; rows: { who: string; what: string }[] }[] = [
    { head: 'AT RISK FIRST', rows: riskAx.slice(0, 8).map((a) => ({ who: a.who, what: a.riskWhy })) },
    { head: 'STILL OPEN BY TAB', rows: (Object.keys(byTab) as TabKey[]).map((k) => ({ who: TAB_LABEL[k], what: `${byTab[k]} open` })) },
    { head: 'SNOOZED', rows: snoozed.slice(0, 8).map((a) => ({ who: a.who, what: `${TAB_LABEL[a.tab]} · ${a.label} · ${a.exit?.why ?? ''} · back ${clockText(a.exit?.until ?? now)}` })) },
    { head: 'REFUSED', rows: refused.slice(0, 8).map((a) => ({ who: a.who, what: `${TAB_LABEL[a.tab]} · ${a.label} · ${a.exit?.why ?? ''}` })) },
    { head: 'HANDED OFF', rows: handed.slice(0, 8).map((a) => ({ who: a.who, what: `${TAB_LABEL[a.tab]} · ${a.label} → ${a.exit?.to ?? ''}` })) },
  ];

  // Who an incident can be about: the building's residents, with the room the med pass knows.
  const roomOf = new Map((meds.data?.meds ?? []).filter((m) => m.room).map((m) => [m.resident_id, m.room as string]));
  const incidentResidents = (roster.data?.residents ?? []).map((r) => ({ id: r.id, label: [r.name ?? 'Resident', roomOf.get(r.id)].filter(Boolean).join(' · ') }));
  const chargeOn = (onShift.data?.people ?? []).find((p) => p.onCallRoles.includes('CHARGE_NURSE') && p.userId !== meId) ?? null;
  const chargeName = chargeOn?.name ?? null;
  // Chart and Resident trends: Care Staff chart for this shift's residents (the server refuses
  // anyone else); the Facility Admin picks from the building.
  const pocResidents = baseLens === 'staff' ? (assignment.data?.assignment?.residents ?? []) : (roster.data?.residents ?? []).map((r) => ({ id: r.id, name: r.name ?? null, room: roomOf.get(r.id) ?? null }));

  // ── V9 "Pulled away" cover ────────────────────────────────────────────────
  const coverData = cover.data;
  const tagByKey = coverTags(coverData?.received ?? []);
  const coverTag = (key: string) => tagByKey.get(key) ?? null;
  const lookCover = (ref: { tab: 'tasks' | 'medpass'; sourceId: string }): CoverLookup => {
    if (ref.tab === 'tasks') {
      const i = (measures.data?.items ?? []).find((x) => x.itemRef.sourceId === ref.sourceId || x.id === ref.sourceId);
      return i ? { who: i.residentName, room: i.room, task: i.task, ladder: i.ladder ?? null } : { who: null, room: null, task: null };
    }
    const m = (meds.data?.meds ?? []).find((x) => x.medication_id === ref.sourceId);
    return m ? { who: m.resident_name, room: m.room ?? null, task: [m.name, m.dosage].filter(Boolean).join(' ') || null, ladder: null } : { who: null, room: null, task: null };
  };
  const firstName = (n: string) => n.split(/\s+/)[0];
  const holderOf = (ownerId: string | null, ownerRole: string | null) =>
    ownerId ? (ownerId === meId ? 'you' : people.find((p) => p.userId === ownerId)?.name ?? 'a colleague') : ownerRole ? (OWNER_WORDS[ownerRole] ?? ownerRole.toLowerCase().replace(/_/g, ' ')) : 'a colleague';
  const received = receiverCards(coverData?.received ?? [], lookCover, now);
  const pendingN = received.reduce((a, c) => a + c.pending.length, 0);
  // What of mine moved and is still with someone else: once it is all done or taken back, the card goes.
  const given = senderCards((coverData?.given ?? []).filter((g) => g.ownerId !== meId), lookCover, (g) => holderOf(g.ownerId, g.ownerRole), new Map()).filter((c) => c.openN > 0);
  const coverOff = { disabled: writesOff || busy, title: offReason ?? undefined };
  const acceptAll = (refs: { tab: 'tasks' | 'medpass'; sourceId: string; occurrence: string | null }[]) =>
    void act(async () => {
      await worklistCoverAccept(client, { body: { itemRefs: refs } });
      cover.reload();
    });
  const passOn = (refs: { tab: 'tasks' | 'medpass'; sourceId: string; occurrence: string | null }[]) =>
    void act(async () => {
      for (const itemRef of refs) await worklistCoverPass(client, { body: { itemRef } });
      cover.reload();
    });
  const takeBack = (causeId: string, itemRef?: CoverItemRef) =>
    void act(async () => {
      await worklistCoverTakeBack(client, { body: itemRef ? { causeId, itemRef } : { causeId } });
      cover.reload();
    });
  const meWho = lens === 'admin' ? `${session?.user.name ?? 'Your'}’s To do · Facility Admin` : 'Moved to you';
  const receiverViews = received.map((c) => (
    <ReceiverCardView key={c.causeId} card={c} who={meWho} ro={shownRo} onAccept={() => acceptAll(c.pending)} onPass={() => passOn(c.pending)} off={coverOff} />
  ));

  // ── V9 staffing line (Facility Admin and the read-only roles) ─────────────
  const staffingShown = (baseLens === 'admin' || baseLens === 'ro') && !preview;
  const briefFeed = shiftBriefFeed();
  const calls = callOutFeed();
  const cov = coverage.data ? { coveredOnTime: coverage.data.coveredOnTime, live: coverage.data.live } : null;
  const covCard = coverageCard(cov);
  const nameOfHolder = (k: string) => {
    const p = people.find((x) => x.userId === k);
    return p ? firstName(p.name) : OWNER_WORDS[k] ?? 'colleague';
  };
  const detailsOpen = stOpen ?? (lens === 'admin' && pendingN > 0);

  // ── V8 new medicine watch ─────────────────────────────────────────────────
  const wd = watches.data;
  const stepRows: WatchStepRow[] = wd && wd.view === 'steps' ? watchSteps(wd.watches, now) : [];
  const stepsCanAct = !!wd && wd.view === 'steps' && !wd.readOnly && !shownRo;
  const chart = (residentId: string) => navigate(`/residents?resident=${encodeURIComponent(residentId)}`);
  const stepDone = (r: WatchStepRow) =>
    void act(async () => {
      await medWatchStepDone(client, { params: { id: r.watchId, stepId: r.stepId }, body: {} });
      watches.reload();
    });
  const reportSign = (r: WatchSignRow, sign: string) =>
    void act(async () => {
      const res = await medWatchSign(client, { params: { id: r.watchId }, body: { sign } });
      setReported((p) => ({ ...p, [`${r.watchId}:${sign}`]: clockText(Date.now()) }));
      setPocToast(res.told);
    });
  const watchBlock = (label: string) =>
    !wd ? null : wd.view === 'steps' ? (
      <WatchStepsBlock label={label} head={watchHead(stepRows.length)} sub={wd.escalation} rows={stepRows} canAct={stepsCanAct} onDone={stepDone} onChart={chart} off={coverOff} />
    ) : wd.watches.length > 0 ? (
      <WatchSignsBlock head={wd.title} rows={watchSignRows(wd.watches)} reported={reported} onReport={reportSign} onChart={chart} off={coverOff} />
    ) : null;

  // ── V8 cards with no back end yet (Care Staff) ────────────────────────────
  const creds = myCredentialsFeed();
  const survey = mySurveyFeed();
  const safety = medSafetyFeed();

  // ── V9 views with nothing left, and Select all ────────────────────────────
  const tabOpen = allAx.filter((a) => a.live && a.tab === tab && keep(a));
  const viewEmpty = view !== 'All' && tabOpen.length === 0;
  const selCand = allAx.filter((a) => a.open && a.tab === tab && keep(a));
  const allSel = selCand.length > 0 && selCand.every((a) => sel[a.id]);
  const tabWord = (k: string) => (lens === 'staff' && k === 'requests' ? 'Your tasks' : TAB_LABEL[k as TabKey] ?? k);
  const resumeShown = resumeOn && !!resumePrev && !resumeGone && resumePrev !== tab && tabs.some((t) => t.k === resumePrev);

  const tabProps = { now, ro: shownRo, lens, writesOff, offReason, axActs, keep, act, client, busy, facilityId, tools, coverTag };

  return (
    <div className="wl" data-testid="worklist" style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 auto;min-height:360px;overflow-x:hidden;overflow-y:auto;')}>
      {resumeShown && (
        <ResumeLine
          line={`Pick up where you left off: Worklist · ${tabWord(resumePrev!)}`}
          onGo={() => {
            setTab(resumePrev as TabKey);
            setResumeGone(true);
          }}
          onNo={() => setResumeGone(true)}
        />
      )}
      <ClaimsToSign />
      {preview && (
        <div role="status" className="viewas-banner" data-testid="worklist-preview">
          <span className="viewas-banner__tag">PREVIEW</span>
          <span className="viewas-banner__text">
            <span className="viewas-banner__title">Previewing the Worklist as Care Staff</span>
            <span className="viewas-banner__note">Read only on this page. Nothing you do here is sent, and your own role is unchanged.</span>
          </span>
          <button type="button" className="viewas-banner__stop" onClick={() => setPreview(false)}>
            {`Back to ${ROLE_LABEL[realRole ?? 'ADMIN']}`}
          </button>
        </div>
      )}
      {toolsShown && (
        <NurseToolsRow
          inboxCount={inbox.data ? inbox.data.reports.length : null}
          disabled={!toolsLive}
          title={toolsLive ? undefined : offReason ?? undefined}
          onOpen={(m) => toolsLive && setPoc(m)}
        />
      )}
      <div style={sx('position:relative;flex:0 0 auto;min-width:0;')}>
      <Tabs
        variant="underline"
        label="Worklist"
        value={tab}
        onChange={(k) => {
          setTab(k);
          setMenu(null);
          // A failed action's error belongs to its tab, not to every tab after it.
          setActionError(null);
        }}
        items={tabs.map((t) => ({ key: t.k, label: t.label, count: t.n }))}
      />
      </div>

      <div style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:5px;margin:0 0 8px;min-width:0;')}>
        {(ro || preview) && !preview && (
          <div title={boardText} style={sx('display:flex;align-items:center;gap:12px;flex-wrap:nowrap;min-width:0;height:34px;padding:0 4px 0 12px;border-radius:9px;background:rgba(34,39,46,.05);')}>
            <span style={sx('font-size:16px;font-weight:700;color:#22272E;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;flex:1 1 auto;')}>
              Read-only as {roleName} &middot; staff act on these
            </span>
            {boardFacilities.length > 1 && (
              <select
                aria-label="Building"
                value={facilityId ?? ''}
                onChange={(e) => setPickedFacility(e.target.value)}
                className="wl-building"
                style={sx('flex:0 1 auto;max-width:240px;height:28px;border-radius:7px;border:1px solid rgba(34,39,46,.18);background:#FCFAF4;font-family:inherit;font-size:14px;color:#22272E;padding:0 6px;')}
              >
                {boardFacilities.map((f) => (
                  <option key={f.facilityId} value={f.facilityId}>
                    {f.name ?? 'Building'}
                  </option>
                ))}
              </select>
            )}
            <A
              style="flex:0 0 auto;display:inline-flex;align-items:center;height:28px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;color:#FCFAF4;background:#22272E;text-decoration:none;white-space:nowrap;"
              onClick={() => {
                setPreview(true);
                setTab(null);
              }}
              testId="worklist-preview-open"
            >
              Preview as Care Staff
            </A>
          </div>
        )}
        <ToolsToggle open={tools} onToggle={() => setTools((v) => !v)} />
        {tools && (
        <div style={sx(phone ? 'display:flex;align-items:center;gap:6px 8px;flex-wrap:wrap;min-width:0;' : 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;min-width:0;min-height:36px;')}>
          {!phone && <span style={sx('flex:0 0 auto;font-size:14px;font-weight:800;letter-spacing:.1em;color:#B3261E;white-space:nowrap;')}>AT RISK</span>}
          {atRisk.data && <HonestyText
            flags={[{ basis: atRisk.data.basis }]}
            figure="At risk"
            title="Predicted from time left, items ahead of it, owner and snoozes. Not a measurement."
            style="flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.08em;color:#264C7E;background:rgba(47,92,151,.10);border-radius:4px;padding:2px 6px;white-space:nowrap;"
          />}
          <span style={sx('flex:1 1 0;min-width:0;display:flex;align-items:center;gap:6px;flex-wrap:wrap;height:30px;overflow:hidden;')}>
            {!phone &&
              riskChips.map(({ a, why }) => (
                <button
                  key={a.id}
                  type="button"
                  className="wl-a"
                  onClick={() => goRisk(a)}
                  title={why}
                  style={sx('flex:0 0 auto;display:inline-flex;align-items:center;gap:6px;height:30px;box-sizing:border-box;padding:5px 10px;border-radius:8px;border:1px solid rgba(179,38,30,.3);text-decoration:none;min-width:0;')}
                >
                  <span style={sx('flex:0 0 auto;font-size:16px;font-weight:800;color:#22272E;white-space:nowrap;')}>{a.who}</span>
                  <span style={sx('flex:0 0 auto;font-size:14px;color:#3C454F;white-space:nowrap;')}>{why.split(' · ').slice(0, 2).join(' · ')}</span>
                </button>
              ))}
            {riskAx.length === 0 && !phone && atRisk.data && <span style={sx('font-size:16px;color:#1F6B52;font-weight:700;white-space:nowrap;')}>Nothing predicted to slip this shift</span>}
          </span>
          {(moreRisk || phone) && (
            <A style="flex:0 0 auto;font-size:14px;font-weight:700;color:#B3261E;text-decoration:none;white-space:nowrap;" onClick={() => setView('At risk')}>
              {phone ? (riskAx.length ? `${riskAx.length} at risk →` : 'none ✓') : `See all ${riskAx.length}`}
            </A>
          )}
          {wide && !phone ? (
            <span role="tablist" aria-label="View" style={sx('flex:0 0 auto;display:inline-flex;border:1px solid rgba(34,39,46,.18);border-radius:8px;overflow:hidden;')}>
              {(['All', 'At risk', 'Handed off', 'Selected'] as ViewKey[]).map((v) => (
                <button key={v} type="button" role="tab" aria-selected={view === v} className="wl-a" onClick={() => setView(v)} style={sx('display:inline-flex;align-items:center;height:30px;padding:0 10px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' + (view === v ? 'background:#22272E;color:#FCFAF4;' : 'color:#3C454F;'))}>
                  {v}
                </button>
              ))}
            </span>
          ) : (
            <A
              style="flex:0 0 auto;display:inline-flex;align-items:center;height:32px;padding:0 10px;border-radius:8px;border:1px solid rgba(34,39,46,.18);font-size:14px;font-weight:700;color:#22272E;text-decoration:none;white-space:nowrap;"
              title="Views: All, At risk, Handed off, Selected"
              onClick={() => {
                const VS: ViewKey[] = ['All', 'At risk', 'Handed off', 'Selected'];
                setView(VS[(VS.indexOf(view) + 1) % VS.length]);
              }}
            >
              {`View: ${view}`} &#9662;
            </A>
          )}
          {!shownRo && selCand.length > 1 && (
            <A
              style="flex:0 0 auto;display:inline-flex;align-items:center;height:32px;padding:0 11px;border-radius:8px;border:1px solid rgba(34,39,46,.18);font-size:14px;font-weight:700;color:#22272E;text-decoration:none;white-space:nowrap;"
              onClick={() => setSel(allSel ? {} : Object.fromEntries(selCand.map((a) => [a.id, true])))}
              disabled={writesOff}
              title={offReason ?? undefined}
            >
              {allSel ? 'Clear selection' : `Select all ${selCand.length}`}
            </A>
          )}
          <A
            style="flex:0 0 auto;display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;color:#264C7E;border:1px solid rgba(47,92,151,.4);text-decoration:none;white-space:nowrap;"
            onClick={() => setHoOpen(true)}
            testId="worklist-handoff-open"
          >
            {phone ? 'Handoff' : 'Shift handoff'}
          </A>
          {wide && !phone && (
            <span title="J / K move &middot; D done &middot; S snooze &middot; H hand off &middot; R refused &middot; X select" style={sx('flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;')}>
              Keys: J K D S H X
            </span>
          )}
        </div>
        )}
        {phone && <span style={sx('font-size:14px;color:#54606C;')}>Swipe right: Done &middot; left: Snooze</span>}
        {/* V9 "Pulled away": the person pulled away sees where their work went; the person it
            went to sees it with Accept all (a Facility Admin's sits under the staffing Details). */}
        {lens === 'staff' &&
          given.map((c) => <SenderCardView key={c.causeId} card={c} onTakeBack={() => takeBack(c.causeId)} onTakeOne={(r) => takeBack(c.causeId, r)} off={coverOff} />)}
        {lens === 'staff' && receiverViews.length > 0 && <ReceiverFrame>{receiverViews}</ReceiverFrame>}
        {lens === 'admin' && given.map((c) => <SenderCardView key={c.causeId} card={c} onTakeBack={() => takeBack(c.causeId)} onTakeOne={(r) => takeBack(c.causeId, r)} off={coverOff} />)}
        {viewEmpty && (
          <ViewEmpty
            text={view === 'At risk' ? 'Nothing is at risk of going late right now.' : view === 'Handed off' ? 'Nothing has been handed off this shift.' : 'Nothing is selected. Tick the box on a task, or use Select all.'}
            onAll={() => setView('All')}
          />
        )}
        {selIds.length > 0 && !writesOff && (
          <div data-testid="worklist-bulk" style={sx('display:flex;align-items:center;gap:6px 10px;flex-wrap:wrap;min-width:0;padding:8px 12px;border-radius:10px;background:#22272E;')}>
            {/* The count is what the buttons act on: rows still on the list, not stale picks. */}
            <span style={sx('font-size:16px;font-weight:800;color:#FCFAF4;')}>{`${selAx.length} selected`}</span>
            <span style={sx('flex:1 1 10px;')} />
            <A style={bb + 'background:#1F6B52;color:#fff;'} disabled={!selAx.length} title={selAx.length ? undefined : NOTHING_SELECTED} onClick={() => void exitAction(selAx, { action: 'DONE' }, { st: 'done' }, '✓ Done')}>
              Done all
            </A>
            <A
              style={bb + 'color:#FCFAF4;border:1px solid rgba(252,250,244,.4);'}
              disabled={!selAx.length}
              title={selAx.length ? undefined : NOTHING_SELECTED}
              onClick={() => void exitAction(selAx, { action: 'SNOOZE', reason: 'asleep', snoozeUntil: new Date(Date.now() + 3_600_000).toISOString() }, { st: 'snz', why: 'Asleep · 1h', until: Date.now() + 3_600_000 }, 'Snoozed 1h')}
            >
              Snooze 1h
            </A>
            <A style={bb + 'color:#FCFAF4;border:1px solid rgba(252,250,244,.4);'} disabled={!selAx.length} title={selAx.length ? undefined : NOTHING_SELECTED} onClick={() => void exitAction(selAx, { action: 'HANDOFF', handedTo: { role: 'CHARGE_NURSE' } }, { st: 'hand', to: 'Charge nurse' }, 'Handed to charge nurse')}>
              Hand to charge nurse
            </A>
            <A style={bb + 'color:#9DB4D3;'} onClick={() => setSel({})}>
              Clear
            </A>
          </div>
        )}
      </div>

      {staffingShown && (
        <StaffingSummary line={staffingLine(briefFeed, cov)} open={detailsOpen} onToggle={() => setStOpen(!detailsOpen)}>
          <TonightStaffing
            briefLine={briefFeed ? `Shift brief heard: ${briefFeed.heard} of ${briefFeed.total}${briefFeed.notYet.length ? ` · not yet: ${briefFeed.notYet.join(', ')}` : ''}` : 'Shift brief heard: not tracked yet. Aithora does not record a shift brief or who heard it.'}
            callOutLine={calls ? `Call-outs this month: ${calls.thisMonth} · ${calls.filledInternally} filled internally (average ${calls.avgMinutes} min) · ${calls.byAgency} by agency` : 'Call-outs this month: call-out fill is not connected yet.'}
          />
          <CoverOversight head={covCard.head} sub={covCard.sub} live={cov ? liveLines(cov.live, nameOfHolder) : []}>
            {lens === 'admin' && receiverViews.length > 0 ? receiverViews : null}
          </CoverOversight>
        </StaffingSummary>
      )}

      {actionError && <ErrorState error={actionError} />}

      {tab === 'measures' && (
        <MeasuresTab
          {...tabProps}
          q={measures}
          rows={mRows.filter((r) => keep(r.ax))}
          allRows={mRows}
          groups={measureGroups(mRows.filter((r) => keep(r.ax)).map((r) => r.row), wqMode === 'resident')}
          axById={new Map(mRows.map((r) => [r.row.id, r.ax]))}
          mode={wqMode}
          setMode={setWqMode}
          drafts={drafts}
          setDraft={setDraft}
          capture={capture}
          codeQ={codeQ}
          setCodeQ={setCodeQ}
          scopeAll={scopeAll}
          setScopeAll={setScopeAll}
          codesAll={codesAll.data}
          hits={codeHits.data?.codes ?? []}
          adding={adding}
          setAdding={setAdding}
          newItem={newItem}
          setNewItem={setNewItem}
          residents={roster.data?.residents ?? []}
          ownerOpts={ownerOpts}
          addItem={addItem}
          ladder={measureLadder}
          movingUp={movingUp}
        />
      )}
      <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;min-width:0;padding-right:4px;')}>
        {tab === 'notes' && <NotesTab {...tabProps} q={notes} rows={nRows.filter((r) => keep(r.ax))} allRows={nRows} sign={(residentId) => act(() => partASign(client, { params: { residentId }, body: {} }))} />}
        {tab === 'requests' && (
          <RequestsTab
            {...tabProps}
            q={reqs}
            rows={qRows.filter((r) => keep(r.ax))}
            allRows={qRowsAll}
            filter={reqFilter}
            setFilter={setReqFilter}
            src={reqSrc}
            setSrc={setReqSrc}
            brief={brief}
            setBrief={setBrief}
            ladder={requestLadder}
            movingUp={movingUp}
            ack={(id) => act(() => careRequestAcknowledge(client, { params: { id }, body: needPick && facilityId ? { organizationId: facilityId } : undefined }))}
            done={(id) => act(() => careRequestComplete(client, { params: { id }, body: needPick && facilityId ? { organizationId: facilityId } : undefined }))}
            scheduled={
              lens === 'staff' && mRows.length > 0 ? (
                <div data-screen-label="Your scheduled tasks" style={sx('margin-top:16px;min-width:0;')}>
                  <span style={sx('font-size:18px;font-weight:800;color:#22272E;')}>Your scheduled tasks</span>
                  <MeasureGroups
                    groups={measureGroups(mRows.filter((r) => keep(r.ax)).map((r) => r.row), false)}
                    axById={new Map(mRows.map((r) => [r.row.id, r.ax]))}
                    drafts={drafts}
                    setDraft={setDraft}
                    capture={capture}
                    ro={shownRo}
                    writesOff={writesOff}
                    offReason={offReason}
                    axActs={axActs}
                    ladder={measureLadder}
                    coverTag={coverTag}
                  />
                </div>
              ) : null
            }
            folded={
              lens === 'staff' ? (
                <>
                  <ShiftBriefRow />
                  <MoreRow coverOn />
                </>
              ) : null
            }
            extras={
              <>
                {lens === 'staff' && (
                  <NotConnectedCard
                    label="Worklist · My credentials"
                    head="Your license and training"
                    body={creds ? creds.rows.map((r) => `${r.k}: ${r.v}`).join(' · ') : 'Your license check and training hours show here once the credentials feed is connected.'}
                  />
                )}
                {watchBlock('Worklist · New medicine watch')}
                {lens === 'staff' && (
                  <NotConnectedCard
                    label="Worklist · Survey items"
                    tint
                    head="Survey items for you"
                    body={survey ? survey.rows.map((r) => r.k).join(' · ') : 'Survey readiness items for you show here once they are connected for this building.'}
                  />
                )}
              </>
            }
          />
        )}
        {tab === 'medpass' && (
          <MedPassTab
            {...tabProps}
            q={meds}
            rows={dRows.filter((r) => keep(r.ax))}
            allRows={dRows}
            given={(m) => giveMed(m)}
            notGiven={(m) => !writesOff && setMedSheet({ mode: 'notgiven', med: m })}
            strike={(m) => !writesOff && m.administration_id && setMedSheet({ mode: 'strike', med: m, administrationId: m.administration_id })}
            assisted={(m) => giveMed(m, true)}
            notTaken={(m) => !writesOff && setMedSheet({ mode: 'notgiven', med: m, assisted: true })}
            observe={(m) => !writesOff && setMedSheet({ mode: 'observe', med: m })}
            alsoToday={
              lens === 'admin' ? (
                <AlsoTodayRow
                  chips={[
                    ...(wd && wd.view === 'steps' && stepRows.length > 0 ? [{ k: 'New medicine', n: stepRows.length, target: 'Worklist med pass · New medicine watch' }] : []),
                    { k: 'Back from hospital', n: safety ? safety.discharge.length : null, target: 'Worklist med pass · Medicine safety' },
                    { k: 'Check before giving', n: safety ? safety.precheck.length : null, target: 'Worklist med pass · Medicine safety' },
                    { k: 'Did it work?', n: safety ? safety.prn.length : null, target: 'Worklist med pass · Medicine safety' },
                    { k: 'Pharmacist', n: safety ? safety.pharmacist.length : null, target: 'Worklist med pass · Medicine safety' },
                  ]}
                />
              ) : null
            }
            after={
              lens !== 'staff' ? (
                <>
                  {wd && wd.view === 'steps' ? watchBlock('Worklist med pass · New medicine watch') : null}
                  {lens === 'admin' && <SafetyCards />}
                </>
              ) : null
            }
          />
        )}
        {tab === 'nutrition' && (
          <NutritionTab
            {...tabProps}
            q={nut}
            rows={uViews.filter((r) => keep(r.ax))}
            allRows={nutRowsAll}
            sel={nutSel}
            setSel={setNutSel}
            wt={nutWt}
            setWt={setNutWt}
            ch={nutCh}
            setCh={setNutCh}
            roleName={roleName}
            record={(residentId, lb, reps) =>
              act(async () => {
                await recordWeight(client, { params: { id: residentId }, body: { weightKg: Math.round((lb / 2.20462) * 100) / 100 } });
                await recordChairStand(client, { params: { id: residentId }, body: { reps } });
                setNutWt('');
                setNutCh('');
              })
            }
          />
        )}
        {tab === 'benefits' && (
          <BenefitsTab
            {...tabProps}
            q={bens}
            rows={bRows.filter((r) => keep(r.ax))}
            all={benAll}
            kind={benKind}
            setKind={setBenKind}
            refOpen={benRefOpen}
            setRefOpen={setBenRefOpen}
            reference={benRef.data}
            handle={(id) => act(() => benefitItemUpdate(client, { params: { id }, body: { status: 'complete', ...(needPick && facilityId ? { organizationId: facilityId } : {}) } }))}
            daysLeft={(b) => daysLeft(b, now)}
            isHandled={isHandled}
          />
        )}
        {tab === 'referrals' && lens !== 'staff' && (
          <ReferralsTab
            {...tabProps}
            q={refs}
            rows={fRows.filter((r) => keep(r.ax))}
            all={refAll}
            sel={refSel}
            setSel={setRefSel}
            drafts={refDrafts}
            setDraft={(id: string, d: RefDraft) => setRefDrafts((p) => ({ ...p, [id]: { ...p[id], ...d } }))}
            caseNo={refCase}
            setCaseNo={(id: string, v: string) => setRefCase((p) => ({ ...p, [id]: v }))}
            me={session?.user.name ?? 'You'}
            file={(r: Referral) =>
              act(async () => {
                const d = refDrafts[r.id] ?? {};
                const cl = r.checklist ?? {};
                const phys = d.phys ?? (typeof cl.preserve_evidence === 'string' ? cl.preserve_evidence : undefined);
                await referralUpdate(client, {
                  params: { id: r.id },
                  body: {
                    narrative: d.narrative ?? r.narrative ?? '',
                    checklist: {
                      ...cl,
                      document_observations: true,
                      preserve_evidence: phys ?? true,
                      ensure_safety: true,
                      reporter_attests: true,
                      // The reporter is recording that they made the report.
                      contact_aps: true,
                    },
                    ...(r.contacts ? { jurisdiction: r.contacts.label, apsContact: r.contacts.aps } : {}),
                    status: 'SUBMITTED',
                    ...(needPick && facilityId ? { organizationId: facilityId } : {}),
                  },
                });
              })
            }
            saveCase={(r: Referral, v: string) => act(() => referralUpdate(client, { params: { id: r.id }, body: { apsReference: v } }))}
            checklist={refChecklist.data?.checklist ?? []}
            residents={lens === 'admin' ? roster.data?.residents ?? [] : []}
            create={(residentId: string, observed: string) =>
              act(() => referralCreate(client, { body: { residentId, attestedBasis: observed, ...(needPick && facilityId ? { organizationId: facilityId } : {}) } }))
            }
            saveDraft={(r: Referral) =>
              act(async () => {
                const d = refDrafts[r.id] ?? {};
                const cl = r.checklist ?? {};
                await referralUpdate(client, {
                  params: { id: r.id },
                  body: {
                    narrative: d.narrative ?? r.narrative ?? '',
                    checklist: { ...cl, ...(d.ticks ?? {}), ...(d.phys ? { preserve_evidence: d.phys } : {}) },
                    ...(needPick && facilityId ? { organizationId: facilityId } : {}),
                  },
                });
              })
            }
            refDone={refDone}
            wchk={WCHK}
          />
        )}
      </div>

      {toast && Date.now() - toast.t < 10_000 && (
        <div role="status" style={sx('position:fixed;left:50%;bottom:22px;transform:translateX(-50%);z-index:75;display:flex;align-items:center;gap:14px;padding:10px 16px;border-radius:10px;background:#22272E;color:#FCFAF4;box-shadow:0 10px 30px rgba(34,39,46,.3);')}>
          <span style={sx('font-size:16px;font-weight:700;')}>{toast.text}</span>
          <A style="font-size:16px;font-weight:800;color:#9DB4D3;text-decoration:none;" onClick={() => void undo(toast.ids, toast.refs)}>
            Undo
          </A>
        </div>
      )}
      {medToast && (
        <div role="status" data-testid="med-undo" style={sx('position:fixed;left:50%;bottom:70px;transform:translateX(-50%);z-index:76;display:flex;align-items:center;gap:14px;padding:10px 16px;border-radius:10px;background:#22272E;color:#FCFAF4;box-shadow:0 10px 30px rgba(34,39,46,.3);')}>
          <span style={sx('font-size:16px;font-weight:700;')}>{medToast.text}</span>
          <A style="font-size:16px;font-weight:800;color:#9DB4D3;text-decoration:none;" onClick={() => undoMed(medToast.id)}>
            Undo
          </A>
        </div>
      )}
      {medSheet && (
        <MedPassSheet
          sheet={medSheet}
          staff={onShift.data?.people ?? []}
          onSubmit={submitMedSheet}
          onClose={() => setMedSheet(null)}
          onScan={(m, body) => medPassScan(client, { body: { medicationId: m.medication_id, ...body } })}
          onNotGiven={(m) => setMedSheet({ mode: 'notgiven', med: m })}
        />
      )}
      {poc === 'incident' && (
        <ReportIncidentSheet
          client={client}
          residents={incidentResidents}
          chargeName={chargeName}
          onClose={() => setPoc(null)}
          onFiled={(t) => {
            setPoc(null);
            setPocToast(t);
            inbox.reload();
          }}
        />
      )}
      {poc === 'inbox' && <InboxSheet client={client} q={inbox} now={now} onClose={() => setPoc(null)} onToast={setPocToast} />}
      {poc === 'scan' && (
        <ScanSheet
          client={client}
          meds={meds.data?.meds ?? []}
          onClose={() => setPoc(null)}
          onRecorded={() => meds.reload()}
          onNeeds={(m, needs, scanId, timingReason) => {
            setPoc(null);
            setMedSheet({ mode: 'given', med: m, needs, scanId, timingReason });
          }}
        />
      )}
      {poc === 'count' && <CountSheet client={client} meName={session?.user.name ?? 'You'} onClose={() => setPoc(null)} onToast={setPocToast} />}
      {poc === 'say' && (
        <SayItOnceSheet client={client} organizationId={session?.user.organizationId ?? null} charge={chargeOn} onClose={() => setPoc(null)} onToast={setPocToast} />
      )}
      {poc === 'chart' && <ChartSheet client={client} residents={pocResidents} charge={chargeOn} meName={session?.user.name ?? 'care staff'} onClose={() => setPoc(null)} onToast={setPocToast} />}
      {poc === 'trends' && <TrendsSheet client={client} residents={pocResidents} onClose={() => setPoc(null)} />}
      {pocToast && <PocToast text={pocToast} />}
      {hoOpen && (
        <div style={sx('position:fixed;inset:0;z-index:80;background:rgba(34,39,46,.32);display:flex;align-items:center;justify-content:center;padding:20px;')} onMouseDown={(e) => e.target === e.currentTarget && setHoOpen(false)}>
          <div role="dialog" aria-label="Shift handoff" data-testid="worklist-handoff" style={sx('width:100%;max-width:560px;max-height:86vh;overflow-y:auto;background:#FCFAF4;border-radius:12px;padding:20px 22px;box-shadow:0 18px 48px rgba(34,39,46,.28);display:flex;flex-direction:column;gap:12px;')}>
            <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;')}>
              <span style={sx('font-size:19px;font-weight:800;color:#22272E;')}>Shift handoff</span>
              <span style={sx('font-size:16px;color:#54606C;')}>{`${clockText(now)} · ${roleName}${ro || preview ? ' (preview)' : ''}`}</span>
            </div>
            <div style={sx('display:flex;gap:6px 18px;flex-wrap:wrap;')}>
              {(
                [
                  ['still open', openAx.length, '#22272E'],
                  ['at risk', riskAx.length, riskAx.length ? '#B3261E' : '#1F6B52'],
                  ['snoozed', snoozed.length, '#3C454F'],
                  ['refused', refused.length, '#3C454F'],
                ] as [string, number, string][]
              ).map(([k, v, c]) => (
                <span key={k} style={sx('display:inline-flex;align-items:baseline;gap:6px;')}>
                  <span style={sx(`font-size:20px;font-weight:800;color:${c};font-variant-numeric:tabular-nums;`)}>{v}</span>
                  <span style={sx('font-size:16px;color:#3C454F;')}>{k}</span>
                </span>
              ))}
            </div>
            {hoGroups.map((g) => (
              <div key={g.head} style={sx('display:flex;flex-direction:column;min-width:0;')}>
                <span style={sx('font-size:14px;font-weight:800;letter-spacing:.1em;color:#54606C;padding-bottom:4px;border-bottom:1px solid rgba(34,39,46,.12);')}>{g.head}</span>
                {g.rows.map((r, i) => (
                  <span key={i} style={sx('display:flex;gap:10px;align-items:baseline;flex-wrap:wrap;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
                    <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{r.who}</span>
                    <span style={sx('font-size:16px;color:#3C454F;min-width:0;flex:1 1 200px;')}>{r.what}</span>
                  </span>
                ))}
                {g.rows.length === 0 && <span style={sx('font-size:16px;color:#54606C;padding:6px 0;')}>None</span>}
              </div>
            ))}
            <div style={sx('display:flex;align-items:center;justify-content:flex-end;gap:10px;flex-wrap:wrap;margin-top:4px;')}>
              {hoSent && <span style={sx('font-size:16px;font-weight:700;color:#1F6B52;')}>{`✓ Sent ${clockText(hoSent)} to the incoming charge nurse`}</span>}
              <A style="display:inline-flex;align-items:center;min-height:44px;padding:0 16px;border-radius:8px;font-size:16px;font-weight:700;color:#22272E;text-decoration:none;border:1px solid rgba(34,39,46,.2);" onClick={() => setHoOpen(false)}>
                Close
              </A>
              {!shownRo && !hoSent && (
                <A
                  style="display:inline-flex;align-items:center;min-height:44px;padding:0 18px;border-radius:8px;font-size:16px;font-weight:700;color:#fff;background:#2F5C97;text-decoration:none;"
                  onClick={() => void sendHandoff()}
                  disabled={writesOff || busy}
                  title={offReason ?? undefined}
                >
                  Send to incoming charge nurse
                </A>
              )}
            </div>
          </div>
        </div>
      )}
      {ro && board.error && <ErrorState error={board.error} onRetry={board.reload} />}
    </div>
  );
}
